import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { signInWithCustomToken, onAuthStateChanged, User, ParsedToken } from 'firebase/auth';
import { collection, doc, documentId, getDoc, getDocs, query, where } from 'firebase/firestore';
import { auth, db } from '../../../firebaseConfig';
import { useNavigate, useLocation } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FirebaseError } from 'firebase/app';

interface SubAgentUser extends User {
  subAgentId?: string;
  companyId?: string;
}

interface CustomClaims extends ParsedToken {
  subAgentId: string;
  companyId: string;
  role: string;
}

interface SubAgentAuthContextType {
  currentSubAgent: SubAgentUser | null;
  subAgentLogin: (username: string, password: string) => Promise<void>;
  subAgentLogout: () => Promise<void>;
  loading: boolean;
  subAgentData: any | null;
  subAgentPermissions: any | null;
}

export const SubAgentAuthContext = createContext<SubAgentAuthContextType | undefined>(undefined);

export const SubAgnetAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentSubAgent, setCurrentSubAgent] = useState<SubAgentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [subAgentData, setSubAgentData] = useState<any | null>(null);
  const [subAgentPermissions, setSubAgentPermissions] = useState<any | null>(null);
  const navigate = useNavigate();
  const functions = getFunctions();

  const subAgentLogin = async (username: string, password: string) => {
    try {

      const createCustomToken = httpsCallable(functions, 'subAgentCustomToken');
      const result = await createCustomToken({ username, password });

      const { token, user } = result.data as { token: string; user: any };

      // Sign in with the custom token
      await signInWithCustomToken(auth, token);

      // Store subagent-specific data
      setSubAgentData(user);

      localStorage.setItem('isSubAgentAuthenticated', 'true');
    } catch (error) {
      console.error('Sub Agent login error:', error);
      throw error;
    }
  };


  const subAgentLogout = async () => {
    try {
      await auth.signOut();
      localStorage.removeItem('isSubAgentAuthenticated');
      setCurrentSubAgent(null);
      setSubAgentData(null);
      navigate('/');
    } catch (error) {
      console.error('Sub Agent logout error:', error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          // Get the ID token result to check claims
          const idTokenResult = await user.getIdTokenResult();
          const claims = idTokenResult.claims as CustomClaims;


          if (claims.role === 'subAgent') {
            const subAgentUser = user as SubAgentUser;
            subAgentUser.subAgentId = claims.subAgentId;
            subAgentUser.companyId = claims.companyId;

            // Fetch additional subAgent data if needed
            const subAgentDocRef = doc(db,
              `companies/${claims.companyId}/subAgents/${claims.subAgentId}`
            );
            const subAgentDoc = await getDoc(subAgentDocRef);


            if (subAgentDoc.exists()) {
              setSubAgentData(subAgentDoc.data());
              setCurrentSubAgent(subAgentUser);

              // Fetch subAgent permissions
              const permissionId = subAgentDoc.data()?.permissionId;

              try {
                const permissionQuery = query(
                  collection(db, 'companies', claims.companyId, 'subAgentPermissions'),
                  where(documentId(), '==', permissionId)
                );

                const permissionSnapshot = await getDocs(permissionQuery);

                // Set permissions
                const permissionsDoc = permissionSnapshot.docs[0];
                if (permissionsDoc.exists()) {
                  setSubAgentPermissions(permissionsDoc.data());
                }
              } catch (error) {
                console.error('Error fetching sub-agent permissions:', error);

                // Handle Firebase-specific errors
                if (error instanceof FirebaseError) {
                  switch (error.code) {
                    case 'permission-denied':
                      console.warn('Permission denied. Logging out sub-agent.');
                      break;
                    default:
                      console.warn('Unexpected Firebase error.');
                  }
                }
              }

            } else {
              await subAgentLogout();
            }
          } else {
            // Not a subAgent user, set states to null without navigation
            setCurrentSubAgent(null);
            setSubAgentData(null);
          }
        } else {
          setCurrentSubAgent(null);
          setSubAgentData(null);
        }
      } catch (error) {
        console.error('Error in auth state change:', error);
        setCurrentSubAgent(null);
        setSubAgentData(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const value = {
    currentSubAgent,
    subAgentLogin,
    subAgentLogout,
    loading,
    subAgentData,
    subAgentPermissions,
  };

  return (
    <SubAgentAuthContext.Provider value={value}>
      {!loading && children}
    </SubAgentAuthContext.Provider>
  );
};

export const 

useSubAgentAuth = () => {
  const context = useContext(SubAgentAuthContext);
  if (context === undefined) {
    throw new Error('useSubAgentAuth must be used within a SubAgnetAuthProvider');
  }
  return context;
};

export const ProtectedSubAgentRoute: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { currentSubAgent, loading } = useSubAgentAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    document.body.scrollTop = 0; // For Safari
    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
  }, [ location.pathname ]);

  useEffect(() => {
    if (!loading) {
      if (!currentSubAgent && !localStorage.getItem('isSubAgentAuthenticated')) {
        // Redirect to login if not authenticated
        navigate('/', { state: { from: location } });
      } else if (currentSubAgent && location.pathname === '/') {
        // Redirect to dashboard if already authenticated and trying to access login
        navigate('/subagent/dashboard');
      }
    }
  }, [currentSubAgent, loading, navigate, location]);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
    </div>;
  }

  return currentSubAgent ? <>{children}</> : null;
};